/**
 * 新建 / 编辑孩子（与 iOS Me/ChildEditSheet.swift 一致）：名字、年级、免打扰时段、播报语速；编辑时可以删除孩子（确认两次）。
 * 设置用 PATCH 合并：只传免打扰和语速，不会覆盖语音助手等其它设置。
 */
import { useState } from "react";
import { confirm, deleteEntity, refreshDevices, run, sessionStore, setSelectedChild, toast, writeEntity, type Child } from "../../core";
import { Button, ChoiceChip, FormCard, ModalHeader, RowDivider, SectionLabel, Sheet, SheetBody, Switch, ValueRow } from "../../ui";
import { GRADES, gradeName } from "./util";

export function ChildEditSheet({ open, child, onClose, onSaved }: { open: boolean; child: Child | null; onClose: () => void; onSaved?: (c: Child) => void }) {
  return (
    <Sheet open={open} onClose={onClose} persistent label={child ? "编辑孩子" : "添加孩子"}>
      <ChildEditBody child={child} onClose={onClose} onSaved={onSaved} />
    </Sheet>
  );
}

function speedLabel(v: number): string {
  return v < 0.4 ? "慢" : v > 0.6 ? "快" : "适中";
}

function ChildEditBody({ child, onClose, onSaved }: { child: Child | null; onClose: () => void; onSaved?: (c: Child) => void }) {
  const [name, setName] = useState(child?.name ?? "");
  const [grade, setGrade] = useState<string | null>(child?.grade ?? null);
  const [quietOn, setQuietOn] = useState(child?.settings?.quietStart != null);
  const [quietStart, setQuietStart] = useState(child?.settings?.quietStart ?? "21:30");
  const [quietEnd, setQuietEnd] = useState(child?.settings?.quietEnd ?? "07:00");
  const [speechRate, setSpeechRate] = useState(child?.settings?.speechRate ?? 0.5);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();

  async function save() {
    if (!trimmed) return;
    if (quietOn && (!quietStart || !quietEnd)) {
      toast("请填写免打扰的开始和结束时间", { error: true });
      return;
    }
    setBusy(true);
    const body = {
      name: trimmed,
      grade,
      settings: {
        quietStart: quietOn ? quietStart : null,
        quietEnd: quietOn ? quietEnd : null,
        speechRate: Math.round(speechRate * 100) / 100,
      },
    };
    const c = await run(() => (child ? writeEntity("children", "PATCH", `children/${child.id}`, body) : writeEntity("children", "POST", "children", body)), "已保存");
    setBusy(false);
    if (!c) return;
    if (!sessionStore.getState().selectedChildId) setSelectedChild(c.id);
    onSaved?.(c);
    onClose();
  }

  async function remove() {
    if (!child) return;
    const first = await confirm({ title: `删除「${child.name}」？`, message: "孩子的平板会同时被解绑。", confirmText: "删除孩子", danger: true });
    if (!first) return;
    const second = await confirm({
      title: "再确认一次",
      message: `删除后${child.name}的日程、学习安排、积分和卡牌都看不到了，且不能恢复。`,
      confirmText: "确定删除",
      danger: true,
    });
    if (!second) return;
    setBusy(true);
    const ok = await run(async () => {
      await deleteEntity("children", child.id, `children/${child.id}`);
      return true;
    }, "已删除");
    setBusy(false);
    if (!ok) return;
    if (sessionStore.getState().selectedChildId === child.id) setSelectedChild(null);
    void refreshDevices();
    onClose();
  }

  return (
    <>
      <ModalHeader title={child ? "编辑孩子" : "添加孩子"} actionTitle="保存" actionEnabled={!!trimmed} busy={busy} onCancel={onClose} onAction={() => void save()} />
      <SheetBody gap={12}>
        <FormCard style={{ padding: "12px 16px" }}>
          <label className="me-name-field">
            <span>昵称</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：朵朵" maxLength={20} autoFocus={!child} />
          </label>
        </FormCard>
        <SectionLabel>年级</SectionLabel>
        <div className="me-chips">
          <ChoiceChip selected={grade == null} onClick={() => setGrade(null)} unselectedBg="var(--kp-surface)">
            不填
          </ChoiceChip>
          {GRADES.map((g) => (
            <ChoiceChip key={g} selected={grade === g} onClick={() => setGrade(g)} unselectedBg="var(--kp-surface)">
              {gradeName(g)}
            </ChoiceChip>
          ))}
        </div>
        <SectionLabel>平板设置</SectionLabel>
        <FormCard>
          <Switch checked={quietOn} onChange={setQuietOn} label="免打扰时段" />
          {quietOn && (
            <>
              <RowDivider />
              <ValueRow title="从">
                <input type="time" className="me-time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} aria-label="免打扰开始时间" />
              </ValueRow>
              <RowDivider />
              <ValueRow title="到">
                <input type="time" className="me-time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} aria-label="免打扰结束时间" />
              </ValueRow>
            </>
          )}
          <RowDivider />
          <div className="me-speed">
            <div>
              <span>播报语速</span>
              <span>{speedLabel(speechRate)}</span>
            </div>
            <input type="range" min={0.2} max={0.8} step={0.05} value={speechRate} onChange={(e) => setSpeechRate(Number(e.target.value))} aria-label="播报语速" />
          </div>
        </FormCard>
        <p className="me-note">免打扰时段内不播报定时提醒；家长发即时提醒时会再确认一次。</p>
        {child && (
          <Button variant="soft" block height={48} className="me-danger-btn" disabled={busy} onClick={() => void remove()}>
            删除孩子
          </Button>
        )}
      </SheetBody>
    </>
  );
}

